import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, createElementBlock as _createElementBlock, Transition as _Transition, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "relative space-y-6 lg:space-y-8 px-3 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)]" }
const _hoisted_2 = { class: "flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" }
const _hoisted_3 = { class: "flex items-center gap-2" }
const _hoisted_4 = { class: "hidden sm:inline" }
const _hoisted_5 = {
  key: 0,
  class: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6"
}
const _hoisted_6 = { class: "flex gap-2 -mt-2 mb-4 overflow-x-auto pb-2" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = {
  key: 0,
  class: "bg-red-50 text-red-600 p-4 rounded-xl border border-red-200"
}
const _hoisted_9 = {
  key: 1,
  class: "card border border-gray-200/50 dark:border-gray-700/50 shadow-sm sticky bottom-0 z-30"
}
const _hoisted_10 = { class: "p-4" }
const _hoisted_11 = { class: "text-sm text-secondary-text/80 dark:text-secondary-dark-text/80" }
const _hoisted_12 = { class: "font-semibold text-slate-700 dark:text-slate-300" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = { class: "text-sm text-secondary-text/80 dark:text-secondary-dark-text/80" }
const _hoisted_15 = { class: "font-semibold text-slate-700 dark:text-slate-300" }
const _hoisted_16 = ["disabled"]

import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { FolderPlus, Loader2, Eye, EyeOff, FileText, CheckCircle, XCircle } from 'lucide-vue-next'
import { materialService } from '@/services/material.service'
import api from '@/services/api'
import type { Material } from '@/config/types/material'
import MaterialTable from '@/components/materials/MaterialTable.vue'
import MaterialFormModal from '@/components/materials/MaterialFormModal.vue'
import MaterialDetailsModal from '@/components/materials/MaterialDetailsModal.vue'
import ViewersEditModal from '@/components/materials/ViewersEditModal.vue'
import Modal from '@/components/layout/Modal.vue'
import TableControls from '@/components/layout/TableControls.vue'
import PaginationBar from '@/components/layout/PaginationBar.vue'
import StatCard from '@/components/layout/StatCard.vue'
import { useToast } from 'vue-toastification'

const toastTimeout = 5000

export default /*@__PURE__*/_defineComponent({
  __name: 'AdminMaterialsView',
  setup(__props) {

const toast = useToast()
const router = useRouter()

const materials = ref<Material[]>([])
const loading = ref(false)
const error = ref('')

const showModal = ref(false)
const modalMode = ref<'add'|'edit'>('add')
const modalError = ref('')
const modalLoading = ref(false)
const currentForm = reactive({
    id: 0,
    title: '',
    description: '',
    type: 'Doc File' as 'Online form' | 'URL Link' | 'Doc File',
    source: null as File | string | null,
    url_link: '',
    status: 'active' as 'active' | 'inactive',
    position_viewers: [] as number[],
    user_viewers: [] as number[]
})

const showDeleteConfirm = ref(false)
const materialToDelete = ref<number | null>(null)
const deleteLoading = ref(false)

const pendingDeleteName = computed(() => {
    if (materialToDelete.value === null) return 'this material'
    const m = materials.value.find(item => item.id === materialToDelete.value)
    return m?.title || 'this material'
})

const cancelDelete = () => {
    showDeleteConfirm.value = false
    materialToDelete.value = null
}

const showToggleConfirm = ref(false)
const toggleLoading = ref(false)
const toggleAction = ref<'activate' | 'deactivate'>('activate')
const pendingToggleMaterial = ref<Material | null>(null)

const pendingToggleName = computed(() => pendingToggleMaterial.value?.title || 'this material')

const cancelToggle = () => {
    showToggleConfirm.value = false
    pendingToggleMaterial.value = null
}

const showDetailsModal = ref(false)
const selectedMaterial = ref<Material | null>(null)

const showViewersModal = ref(false)
const viewersEditId = ref(0)
const viewersEditPositionIds = ref<number[]>([])
const viewersEditUserIds = ref<number[]>([])
const viewersModalLoading = ref(false)

const showStats = ref(false)

// Stats
const stats = computed(() => {
    const list = materials.value || []
    return {
        total: list.length,
        active: list.filter(m => m.status === 'active').length,
        inactive: list.filter(m => m.status === 'inactive').length,
    }
})

// Filters (client-side)
const searchQuery = ref('')
const statusFilter = ref('')
const typeFilter = ref('')

const statusOptions = [
    { label: 'All', value: '' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
]

const typeOptions = [
    { label: 'All Types', value: '' },
    { label: 'Document Files', value: 'Doc File' },
    { label: 'URL Links', value: 'URL Link' },
    { label: 'Online Forms', value: 'Online form' }
]

// Status filter
const statusFiltered = computed(() => {
    const list = materials.value || []
    if (!statusFilter.value) return list
    return list.filter(m => m.status === statusFilter.value)
})

// Type filter
const typeFiltered = computed(() => {
    const list = statusFiltered.value
    if (!typeFilter.value) return list
    return list.filter(m => m.type === typeFilter.value)
})

// Search filter
const searchFiltered = computed(() => {
    const list = typeFiltered.value
    if (!searchQuery.value.trim()) return list
    const query = searchQuery.value.toLowerCase()
    return list.filter(m => {
        const title = (m.title || '').toLowerCase()
        const description = (m.description || '').toLowerCase()
        return title.includes(query) || description.includes(query)
    })
})

// Pagination (client-side)
const perPage = ref(10)
const currentPage = ref(1)
const filteredTotalPages = computed(() => Math.max(1, Math.ceil(searchFiltered.value.length / perPage.value)))
const filteredTotal = computed(() => searchFiltered.value.length)

const paginatedMaterials = computed(() => {
    const start = (currentPage.value - 1) * perPage.value
    const end = start + perPage.value
    return searchFiltered.value.slice(start, end)
})

// Reset page when filters change
watch([statusFilter, searchQuery, typeFilter, perPage], () => {
    currentPage.value = 1
})

const goToPage = (page: number) => {
    currentPage.value = page
}

const fetchMaterials = async () => {
    loading.value = true
    error.value = ''
    try {
        const response = await materialService.fetchAll({ per_page: 1000 })
        materials.value = Array.isArray(response) ? response : (Array.isArray(response.data) ? response.data : [])
    } catch (err: any) {
        error.value = err.message || 'Failed to fetch materials'
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    fetchMaterials()
})

const openAddModal = () => {
    modalMode.value = 'add'
    modalError.value = ''
    currentForm.id = 0
    currentForm.title = ''
    currentForm.description = ''
    currentForm.type = 'Doc File'
    currentForm.source = null
    currentForm.url_link = ''
    currentForm.status = 'active'
    currentForm.position_viewers = []
    currentForm.user_viewers = []
    showModal.value = true
}

const handleEdit = (m: Material) => {
    modalMode.value = 'edit'
    modalError.value = ''
    currentForm.id = m.id
    currentForm.title = m.title
    currentForm.description = m.description || ''
    currentForm.type = m.type
    currentForm.source = null
    currentForm.url_link = m.url_link || ''
    currentForm.status = m.status
    currentForm.position_viewers = m.position_viewers?.map(v => v.id) || []
    currentForm.user_viewers = m.user_viewers?.map(v => v.id) || []
    showModal.value = true
}

const handleEditViewers = (m: Material) => {
    viewersEditId.value = m.id
    viewersEditPositionIds.value = m.position_viewers?.map(v => v.id) || []
    viewersEditUserIds.value = m.user_viewers?.map(v => v.id) || []
    showViewersModal.value = true
}

const closeViewersModal = () => {
    showViewersModal.value = false
    viewersModalLoading.value = false
}

const handleSaveViewers = async (data: { materialId: number; positionViewerIds: number[]; userViewerIds: number[] }) => {
    viewersModalLoading.value = true
    try {
        const m = materials.value.find(item => item.id === data.materialId)
        if (!m) throw new Error('Material not found')

        await api.post(`/materials/${data.materialId}`, {
            _method: 'PUT',
            title: m.title,
            type: m.type,
            status: m.status,
            description: m.description || null,
            url_link: m.url_link || null,
            position_viewers: data.positionViewerIds,
            user_viewers: data.userViewerIds,
        })
        toast.success('Viewers updated successfully', { timeout: toastTimeout })
        showViewersModal.value = false
        fetchMaterials()
    } catch (err: any) {
        toast.error(err.response?.data?.message || err.message || 'Failed to update viewers', { timeout: toastTimeout })
    } finally {
        viewersModalLoading.value = false
    }
}

const closeModal = () => {
    showModal.value = false
}

const handleSave = async (form: any) => {
    modalError.value = ''
    modalLoading.value = true
    try {
        const formData = new FormData()
        formData.append('title', form.title)
        if (form.description) formData.append('description', form.description)
        formData.append('type', form.type)
        formData.append('status', form.status)
        
        if (form.type === 'URL Link' || form.type === 'Online form') {
            formData.append('url_link', form.url_link)
        } else if (form.source instanceof File) {
            formData.append('file', form.source)
        }

        if (form.position_viewers.length > 0) {
            form.position_viewers.forEach((vId: number, index: number) => {
                formData.append(`position_viewers[${index}]`, vId.toString())
            })
        } else {
            formData.append('position_viewers', '')
        }
        if (form.user_viewers.length > 0) {
            form.user_viewers.forEach((vId: number, index: number) => {
                formData.append(`user_viewers[${index}]`, vId.toString())
            })
        } else {
            formData.append('user_viewers', '')
        }

        if (modalMode.value === 'add') {
            await materialService.create(formData)
            toast.success('Material uploaded successfully', { timeout: toastTimeout })
        } else {
            await materialService.update(currentForm.id, formData)
            toast.success('Material updated successfully', { timeout: toastTimeout })
        }
        closeModal()
        fetchMaterials()
    } catch (err: any) {
        modalError.value = err.response?.data?.message || err.message || 'An error occurred'
    } finally {
        modalLoading.value = false
    }
}

const handleDelete = (m: Material) => {
    materialToDelete.value = m.id
    showDeleteConfirm.value = true
}

const confirmDelete = async () => {
    if (!materialToDelete.value) return
    deleteLoading.value = true
    try {
        await materialService.delete(materialToDelete.value)
        toast.success('Material deleted successfully', { timeout: toastTimeout })
        showDeleteConfirm.value = false
        materialToDelete.value = null
        if (materials.value.length === 1 && currentPage.value > 1) {
            currentPage.value--
        }
        fetchMaterials()
    } catch (err: any) {
        toast.error(err.message || 'Failed to delete material', { timeout: toastTimeout })
    } finally {
        deleteLoading.value = false
    }
}

const handleToggleStatus = (m: Material) => {
    pendingToggleMaterial.value = m
    toggleAction.value = m.status === 'active' ? 'deactivate' : 'activate'
    showToggleConfirm.value = true
}

const confirmToggleStatus = async () => {
    const m = pendingToggleMaterial.value
    if (!m) return
    toggleLoading.value = true
    try {
        const formData = new FormData()
        formData.append('title', m.title)
        formData.append('type', m.type)
        formData.append('status', m.status === 'active' ? 'inactive' : 'active')
        if (m.description) formData.append('description', m.description)
        if (m.url_link) formData.append('url_link', m.url_link)
        
        m.position_viewers?.forEach((v, index) => formData.append(`position_viewers[${index}]`, v.id.toString()))
        m.user_viewers?.forEach((v, index) => formData.append(`user_viewers[${index}]`, v.id.toString()))

        await materialService.update(m.id, formData)
        toast.success('Status updated successfully', { timeout: toastTimeout })
        showToggleConfirm.value = false
        pendingToggleMaterial.value = null
        fetchMaterials()
    } catch (err: any) {
        toast.error(err.message || 'Failed to update status', { timeout: toastTimeout })
    } finally {
        toggleLoading.value = false
    }
}

const handleView = (m: Material) => {
  if (m.type === 'Online form') {
    router.push({ name: 'view-online-form', params: { id: m.id } })
  } else {
    selectedMaterial.value = m
    showDetailsModal.value = true
  }
}

const handleDownload = async (m: Material) => {
    try {
        const response = await fetch(materialService.getDownloadUrl(m.id), {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        })
        if (!response.ok) throw new Error('Invalid File')
        
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const disposition = response.headers.get('content-disposition')
        let filename = m.title
        if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            const matches = filenameRegex.exec(disposition)
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '')
            }
        } else if (m.file_path) {
            const parts = m.file_path.split('.')
            if (parts.length > 1) {
                filename = `${m.title}.${parts[parts.length - 1]}`
            }
        }
        
        a.download = filename
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
    } catch (err: any) {
        toast.error(err.message || 'Failed to download file', { timeout: toastTimeout })
    }
}

const handlePrint = async (m: Material) => {
    if (!m.file_path) return
    const loadingToast = toast.info('Preparing document for printing...', { timeout: false })
    try {
        await materialService.printFile(m.id)
        toast.dismiss(loadingToast)
        toast.success('Document printed successfully', { timeout: toastTimeout })
    } catch (err: any) {
        toast.dismiss(loadingToast)
        toast.error(err.message || 'Failed to print file', { timeout: toastTimeout })
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[8] || (_cache[8] = _createElementVNode("div", null, [
        _createElementVNode("h1", { class: "text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-secondary" }, " Materials "),
        _createElementVNode("p", { class: "text-sm text-slate-500 dark:text-slate-200 mt-2" }, " Manage all materials, documents, and online forms. ")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showStats.value = !showStats.value)),
          class: "px-4 py-2 rounded-lg font-semibold transition border border-gray-300 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 inline-flex items-center justify-center w-full sm:w-auto"
        }, [
          (!showStats.value)
            ? (_openBlock(), _createBlock(_unref(Eye), {
                key: 0,
                class: "h-5 w-5 mr-2"
              }))
            : (_openBlock(), _createBlock(_unref(EyeOff), {
                key: 1,
                class: "h-5 w-5 mr-2"
              })),
          _createElementVNode("span", _hoisted_4, _toDisplayString(showStats.value ? 'Hide Stats' : 'Show Stats'), 1)
        ]),
        _createElementVNode("button", {
          onClick: openAddModal,
          class: "px-6 py-2 rounded-lg font-semibold text-white transition bg-primary hover:bg-secondary shadow-md hover:opacity-90 flex items-center justify-center w-full sm:w-auto"
        }, [
          _createVNode(_unref(FolderPlus), { class: "h-5 w-5 mr-2" }),
          _cache[7] || (_cache[7] = _createElementVNode("span", { class: "hidden sm:inline" }, "Add New Material", -1))
        ])
      ])
    ]),
    _createVNode(_Transition, { name: "stats" }, {
      default: _withCtx(() => [
        (showStats.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(StatCard, {
                label: "Total Materials",
                value: stats.value.total,
                loading: loading.value,
                icon: _unref(FileText),
                color: "text-blue-600 dark:text-blue-400"
              }, null, 8, ["value", "loading", "icon"]),
              _createVNode(StatCard, {
                label: "Active",
                value: stats.value.active,
                loading: loading.value,
                icon: _unref(CheckCircle),
                color: "text-emerald-600 dark:text-emerald-400"
              }, null, 8, ["value", "loading", "icon"]),
              _createVNode(StatCard, {
                label: "Inactive",
                value: stats.value.inactive,
                loading: loading.value,
                icon: _unref(XCircle),
                color: "text-rose-600 dark:text-rose-400"
              }, null, 8, ["value", "loading", "icon"])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(TableControls, {
      search: searchQuery.value,
      "onUpdate:search": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
      status: statusFilter.value,
      "onUpdate:status": _cache[2] || (_cache[2] = ($event: any) => ((statusFilter).value = $event)),
      perPage: perPage.value,
      "onUpdate:perPage": _cache[3] || (_cache[3] = ($event: any) => ((perPage).value = $event)),
      options: statusOptions,
      filterLabel: "Status",
      placeholder: "Search by title or description...",
      class: "md:sticky md:top-[80px]"
    }, null, 8, ["search", "status", "perPage"]),
    _createElementVNode("div", _hoisted_6, [
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(typeOptions, (type) => {
        return _createElementVNode("button", {
          key: type.value,
          onClick: ($event: any) => (typeFilter.value = type.value),
          class: _normalizeClass(["px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap", typeFilter.value === type.value ? 'bg-primary text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'])
        }, _toDisplayString(type.label), 11, _hoisted_7)
      }), 64))
    ]),
    (error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    _createVNode(MaterialTable, {
      materials: paginatedMaterials.value,
      loading: loading.value,
      totalUnfiltered: materials.value.length,
      onView: handleView,
      onDownload: handleDownload,
      onPrint: handlePrint,
      onEdit: handleEdit,
      onEditViewers: handleEditViewers,
      onToggleStatus: handleToggleStatus,
      onDelete: handleDelete
    }, null, 8, ["materials", "loading", "totalUnfiltered"]),
    (!loading.value && !error.value && filteredTotal.value > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createVNode(PaginationBar, {
              "current-page": currentPage.value,
              "total-pages": filteredTotalPages.value,
              total: filteredTotal.value,
              "per-page": perPage.value,
              "onUpdate:page": goToPage
            }, null, 8, ["current-page", "total-pages", "total", "per-page"])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(MaterialDetailsModal, {
      "is-open": showDetailsModal.value,
      material: selectedMaterial.value,
      onClose: _cache[4] || (_cache[4] = ($event: any) => {showDetailsModal.value = false; selectedMaterial.value = null})
    }, null, 8, ["is-open", "material"]),
    _createVNode(MaterialFormModal, {
      "is-open": showModal.value,
      mode: modalMode.value,
      form: currentForm,
      error: modalError.value,
      loading: modalLoading.value,
      onClose: closeModal,
      onSave: handleSave
    }, null, 8, ["is-open", "mode", "form", "error", "loading"]),
    _createVNode(ViewersEditModal, {
      "is-open": showViewersModal.value,
      "material-id": viewersEditId.value,
      "position-viewer-ids": viewersEditPositionIds.value,
      "user-viewer-ids": viewersEditUserIds.value,
      loading: viewersModalLoading.value,
      onClose: closeViewersModal,
      onSave: handleSaveViewers
    }, null, 8, ["is-open", "material-id", "position-viewer-ids", "user-viewer-ids", "loading"]),
    _createVNode(Modal, {
      modelValue: showDeleteConfirm.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showDeleteConfirm).value = $event)),
      title: "Delete Material"
    }, {
      footer: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200",
          onClick: cancelDelete
        }, " Cancel "),
        _createElementVNode("button", {
          type: "button",
          class: "inline-flex items-center ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-danger hover:bg-danger/90 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed",
          disabled: deleteLoading.value,
          onClick: confirmDelete
        }, [
          (deleteLoading.value)
            ? (_openBlock(), _createBlock(_unref(Loader2), {
                key: 0,
                class: "w-4 h-4 mr-2 animate-spin"
              }))
            : _createCommentVNode("", true),
          _createTextVNode(" " + _toDisplayString(deleteLoading.value ? 'Deleting...' : 'Yes, delete permanently'), 1)
        ], 8, _hoisted_13)
      ]),
      default: _withCtx(() => [
        _createElementVNode("p", _hoisted_11, [
          _cache[9] || (_cache[9] = _createTextVNode(" Are you sure you want to permanently delete ", -1)),
          _createElementVNode("span", _hoisted_12, "\"" + _toDisplayString(pendingDeleteName.value) + "\"", 1),
          _cache[10] || (_cache[10] = _createTextVNode("? This action cannot be undone. ", -1))
        ])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(Modal, {
      modelValue: showToggleConfirm.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showToggleConfirm).value = $event)),
      title: toggleAction.value === 'deactivate' ? 'Deactivate Material' : 'Activate Material'
    }, {
      footer: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200",
          onClick: cancelToggle
        }, " Cancel "),
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(["flex justify-center ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed", toggleAction.value === 'deactivate' ? 'bg-danger hover:bg-danger/90' : 'bg-success hover:bg-success/90']),
          disabled: toggleLoading.value,
          onClick: confirmToggleStatus
        }, [
          (toggleLoading.value)
            ? (_openBlock(), _createBlock(_unref(Loader2), {
                key: 0,
                class: "w-4 h-4 mr-2 animate-spin"
              }))
            : _createCommentVNode("", true),
          _createTextVNode(" " + _toDisplayString(toggleLoading.value ? 'Processing...' : `Yes, ${toggleAction.value === 'deactivate' ? 'deactivate' : 'activate'}`), 1)
        ], 10, _hoisted_16)
      ]),
      default: _withCtx(() => [
        _createElementVNode("p", _hoisted_14, [
          _createTextVNode(" Are you sure you want to " + _toDisplayString(toggleAction.value === 'deactivate' ? 'deactivate' : 'activate') + " ", 1),
          _createElementVNode("span", _hoisted_15, "\"" + _toDisplayString(pendingToggleName.value) + "\"", 1),
          _cache[11] || (_cache[11] = _createTextVNode("? ", -1))
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})